<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Transactions..."
    button-location="top"
    @submit="getTransactions()"
  >
    <template #title>
      All Gourmet Transactions
    </template>

    <div class="info-comment">
      Row of red text means a failed charge
    </div>

    <template #top-form>
      <div
        v-if="!isFamilyDashboard"
        class="row align-items-center"
      >
        <label class="col-sm-4 control-label">Phone Number*</label>
        <phone-number-input
          id="customer-phone"
          v-model="phoneNumber"
          class="col-sm-4"
          :page-id="pageId"
          :required="true"
        />
      </div>

      <div
        v-if="!isFamilyDashboard"
        class="form-submit-button-group pb-10 mobile-pb-10"
      >
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="status.isLoading || !isValidSearchInput"
        >
          Get Transactions
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="getTransactions"
        />
      </div>

      <message-html :result-html="status.resultHtml" />
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="transactions"
        :highlight-list="failedTransactions"
        :table-columns="isFamilyDashboard ? familyTableColumns : tableColumns"
      />
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, Ref, reactive, computed, watch } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import transactionUtility from '@/Utils/transactionUtility';
import { TransactionType } from '@/models/transaction';
import { TableColumnType, HighlightTableItemType } from '@/models/table';
import { transactionService } from '@/services/transaction';

const pageId: string = 'gourmet-transaction-list';

const tableColumns: TableColumnType[] = [
  { field: 'id', label: 'Transaction ID', width: 200 },
  { field: 'createdAt', label: 'Date', width: 180, isDateField: true },
  { field: 'currency', label: 'Currency', width: 90 },
  { field: 'amount', label: 'Total', width: 90 },
  { field: 'customerCredits', label: 'Customer Credits', width: 140 },
  { field: 'customerCharge', label: 'Customer Charge', width: 140 },
  { field: 'partnerCredits', label: 'Partner Credits', width: 120 },
  { field: 'partnerCharge', label: 'Partner Charge', width: 130 },
  { field: 'statusText', label: 'Status', width: 180, isCapitalize: true },
  { field: 'description', label: 'Description', width: 250 },
  { field: 'notes', label: 'notes', width: 250 },
  { field: 'associatedObjectType', label: 'Type', width: 100 },
  { field: 'associatedObjectId', label: 'User ID', width: 200 },
  { field: 'ipAddress', label: 'IP Address', width: 120 },
  { field: 'ipCountry', label: 'IP Country', width: 120 },
  { field: 'device', label: 'Device', width: 100 },
  { field: 'operatingSystem', label: 'Operating System', width: 150 },
  { field: 'browser', label: 'Browser', width: 120 },
  { field: 'userName', label: 'User Name', width: 150 },
  { field: 'role', label: 'role', width: 120 },
];
const familyTableColumns: TableColumnType[] = [
  { field: 'id', label: 'Transaction ID', width: 200 },
  { field: 'createdAt', label: 'Date', width: 180, isDateField: true },
  { field: 'currency', label: 'Currency', width: 90 },
  { field: 'amount', label: 'Total', width: 90 },
  { field: 'customerCredits', label: 'Customer Credits', width: 140 },
  { field: 'customerCharge', label: 'Customer Charge', width: 140 },
  { field: 'partnerCredits', label: 'Partner Credits', width: 120 },
  { field: 'partnerCharge', label: 'Partner Charge', width: 130 },
  { field: 'statusText', label: 'Status', width: 180, isCapitalize: true },
  { field: 'description', label: 'Description', width: 250 },
  { field: 'notes', label: 'notes', width: 250 },
];

export default {
  emits: ['set-empty-order'],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const { customerPhoneNumber } = useCustomer();
    const { isFamilyDashboard } = useDashboard();

    const phoneNumber: Ref<string> = ref('');
    const transactions: Ref<TransactionType[]> = ref([]);
    const failedTransactions: Ref<HighlightTableItemType[]> = ref([]);
    const status = reactive({
      pageLimit: 10,
      currentPage: 1,
      totalPages: 1,
      isLoading: false,
      resultHtml: '',
    });

    const isValidSearchInput = computed(() => util.isPhoneNumberValid(phoneNumber.value));

    const getTransactions = async (currentPage: number = 1) => {
      const offset = (currentPage - 1) * status.pageLimit;

      failedTransactions.value = [];
      status.currentPage = currentPage;
      status.resultHtml = '';
      status.isLoading = true;

      try {
        const response = await transactionService.fetchGourmetTransactions({
          phoneNumber: phoneNumber.value,
          pagination: {
            limit: status.pageLimit,
            offset,
          },
        });

        transactions.value = response.rows.map((transaction: TransactionType, index: number) => {
          if (transaction.status === 'failed') {
            failedTransactions.value.push({
              index,
              class: 'text-red',
              type: 'row',
            });
          }

          transaction.orderIndex = index;
          return transactionUtility.formatTransactionData(transaction);
        });

        status.totalPages = Math.ceil(response.count / status.pageLimit) || 1;

        emit('set-empty-order', response.count === 0);
      } catch (error) {
        transactions.value = [];
        status.currentPage = 1;
        status.totalPages = 1;
        status.resultHtml = util.getGraphqlError(error);
      }

      status.isLoading = false;
    };

    const resetForm = () => {
      status.resultHtml = '';
      status.currentPage = 1;
      status.totalPages = 1;
      transactions.value = [];
      phoneNumber.value = '';
    };

    watch(
      () => customerPhoneNumber.value,
      (value: string) => {
        if (value) {
          phoneNumber.value = value;
          getTransactions();
        }
      },
      {
        immediate: true,
      },
    );

    return {
      pageId,
      util,

      isFamilyDashboard,
      phoneNumber,
      transactions,
      failedTransactions,
      status,
      isValidSearchInput,

      tableColumns,
      familyTableColumns,

      getTransactions,
      resetForm,
    };
  },
};
</script>
